import * as React from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export interface BaseFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  submitLabel?: string
  cancelLabel?: string
  isSubmitting?: boolean
  submitDisabled?: boolean
  hideSubmitButton?: boolean
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl'
  onCancel?: () => void
  children: React.ReactNode
}

/**
 * BaseFormDialog - Reusable dialog wrapper for form-based dialogs
 *
 * Provides consistent styling and behavior for form dialogs throughout the app.
 * Handles form submission state, button styling, and common dialog patterns.
 *
 * @param open - Whether the dialog is open
 * @param onOpenChange - Callback when dialog open state changes
 * @param title - Dialog title
 * @param description - Optional dialog description
 * @param submitLabel - Label for submit button (default: "Save")
 * @param cancelLabel - Label for cancel button (default: "Cancel")
 * @param isSubmitting - Whether form is currently submitting
 * @param submitDisabled - Whether submit button should be disabled
 * @param hideSubmitButton - Whether to hide the submit button completely
 * @param maxWidth - Maximum width of dialog (default: "md")
 * @param onCancel - Optional custom cancel handler
 * @param children - Form content and fields
 *
 * @example
 * ```tsx
 * <BaseFormDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Create Item"
 *   description="Add a new item to your workspace"
 *   isSubmitting={isLoading}
 *   submitDisabled={!isValid}
 * >
 *   <form onSubmit={handleSubmit}>
 *     <Input placeholder="Item name" />
 *   </form>
 * </BaseFormDialog>
 * ```
 */
export function BaseFormDialog({
  open,
  onOpenChange,
  title,
  description,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  isSubmitting = false,
  submitDisabled = false,
  hideSubmitButton = false,
  maxWidth = 'md',
  onCancel,
  children,
}: BaseFormDialogProps) {
  const maxWidthClass = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
  }[maxWidth]

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={maxWidthClass}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : (
            <DialogDescription className="sr-only">{title}</DialogDescription>
          )}
        </DialogHeader>

        {children}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            {cancelLabel}
          </Button>
          {!hideSubmitButton && (
            <Button
              type="submit"
              disabled={submitDisabled || isSubmitting}
              form="dialog-form"
            >
              {isSubmitting ? 'Saving...' : submitLabel}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
